import { apiClient, requiresAuth } from '../client';

export interface SyncResult {
  campus_id: string;
  sync_status: 'completed' | 'failed' | 'partial';
  places_added: number;
  places_updated: number;
  places_removed: number;
  api_calls_used: number;
  sync_duration_ms: number;
  total_places_processed: number;
  errors?: string[];
}

export interface SyncSummary {
  total: number;
  completed: number;
  failed: number;
  partial: number;
  totalPlacesAdded: number;
  totalPlacesUpdated: number;
  totalPlacesRemoved: number;
  totalApiCalls: number;
}

export interface SyncLog {
  _id: string;
  campus_id: {
    _id: string;
    campus_name: string;
  };
  last_sync_date: string;
  places_added: number;
  places_updated: number;
  places_removed: number;
  api_calls_used: number;
  sync_status: string;
  sync_duration_ms: number;
  total_places_processed: number;
  error_message?: string;
  createdAt: string;
}

export interface CampusBoundary {
  type: string;
  coordinates: number[][][];
}

export interface CampusWithSyncStatus {
  _id: string;
  campus_name: string;
  coordinates?: CampusBoundary;
  places_count: number;
  last_sync: {
    date: string;
    status: string;
    places_added: number;
    places_updated: number;
    places_removed: number;
    api_calls_used: number;
  } | null;
  has_coordinates: boolean;
}

export interface SyncOptions {
  forceFullSync?: boolean;
  includeDetails?: boolean;
  maxApiCalls?: number;
}

export interface LastSyncInfo {
  date: string;
  status: string;
  region?: string;
  places_added: number;
  places_updated: number;
  places_removed: number;
  duration_ms: number;
}

export const campusSyncAPI = {
  // Trigger sync for a specific campus
  syncCampus: async (campusId: string, options?: SyncOptions): Promise<SyncResult> => {
    return requiresAuth(async () => {
      const response = await apiClient.post(
        `/api/admin/campus-sync/sync/${campusId}`,
        options || {}
      );
      return response.data.data;
    });
  },

  // Trigger sync for all active campuses
  syncAllCampuses: async (): Promise<{ summary: SyncSummary; results: SyncResult[] }> => {
    return requiresAuth(async () => {
      const response = await apiClient.post('/api/admin/campus-sync/sync-all', {});
      return response.data.data;
    });
  },

  // Get sync logs
  getSyncLogs: async (params?: {
    campus_id?: string;
    sync_status?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ logs: SyncLog[]; pagination: { total: number; limit: number; offset: number } }> => {
    return requiresAuth(async () => {
      const response = await apiClient.get('/api/admin/campus-sync/logs', { params });
      return response.data.data;
    });
  },

  // Get campuses with sync status
  getCampusesWithSyncStatus: async (): Promise<CampusWithSyncStatus[]> => {
    return requiresAuth(async () => {
      const response = await apiClient.get('/api/admin/campus-sync/campuses');
      return response.data.data;
    });
  },

  // Preview campus boundary and search points
  previewCampusBoundary: async (campusId: string): Promise<{
    campus_name: string;
    boundary: CampusBoundary | null;
    search_points: { lat: number; lng: number; radius: number }[];
    bounds: { north: number; south: number; east: number; west: number };
    center: { lat: number; lng: number };
    area_sqm: number;
    area_acres: number;
    search_points_count: number;
  }> => {
    return requiresAuth(async () => {
      const response = await apiClient.get(`/api/admin/campus-sync/campus/${campusId}/preview`);
      return response.data.data;
    });
  },

  // Get scheduler status
  getSchedulerStatus: async (): Promise<{
    places_system: {
      is_initialized: boolean;
      sync_scheduler_enabled: boolean;
      errors: string[];
      warnings: string[];
    };
    last_sync: LastSyncInfo | null;
    recent_syncs_count: number;
    environment: string;
  }> => {
    return requiresAuth(async () => {
      const response = await apiClient.get('/api/admin/campus-sync/scheduler/status');
      return response.data.data;
    });
  },
};

export default campusSyncAPI;
